define('bizzabo/ModelPool',[
    'underscore',
    'bizzabo/consts',
    'bizzabo/models/EventDetails',
    'bizzabo/models/CreatedEventsCollection',
    'bizzabo/models/Person',
    'bizzabo/models/Persons',
    'bizzabo/models/Auth',
    'bizzabo/models/Agenda',
    'bizzabo/models/Partners',
    'bizzabo/models/ServicePlan',
    'bizzabo/models/Polls'
], function(
    _,
    Consts,
    EventDetails,
    CreatedEventsCollection,
    Person,
    Persons,
    Auth,
    Agenda,
    Partners,
    ServicePlan,
    Polls
    ) {

    var ModelPool = function() {

        /***************** Data structures *****************/

        var events = undefined;
        var activeEventCid = undefined;
        var agendas = {};
        var personsPerEvent = {};
        var partnersPerEvent = {};
        var servicePlanPerEvent = {};
        var pollsPerEvent = {};
        var me = undefined;
        var auth = undefined;

        /***************** Public methods *****************/

        var reset = function() {
            events = new CreatedEventsCollection();
            activeEventCid = undefined;
            me = new Person({
                id : '~'
            });
            agendas = {};
            personsPerEvent = {};
            partnersPerEvent = {};
            servicePlanPerEvent = {};
            pollsPerEvent = {};
        };

        var getActiveEvent = function() {
            return events.get(activeEventCid);
        };

        var setActiveEvent = function(activeEvent) {
            if (_.isUndefined(events.get(activeEvent.cid))) {
                events.add(activeEvent);
            }
            activeEventCid = activeEvent.cid;
        };

        var setEvents = function(newEvents) {
            events = newEvents;
        };

        var getEvents = function() {
            return events; // consider returning a clone
        };

        var getNewEvents = function() {
            var newEvents = [];
            _.each(events.models, function(event) {
                if (event.isNew()) {
                    newEvents.push(event);
                }
            }, this);
            return newEvents;
        };

        var getEventDraft = function() {
            var newEvents = getNewEvents();
            if (newEvents.length === 0) {
                var newEvent = new EventDetails({
                    'draftName' : Consts.Strings.eventDraft.DEFAULT_DRAFT_NAME
                });
                events.add(newEvent);
                return newEvent;
            }
            // currently supports a single new event
            return newEvents[0];
        };

        var hasEventDraft = function() {
            var newEvents = getNewEvents();
            return newEvents.length > 0;
        };

        var getEvent = function(eventId) {
            var theEvent = getEvents().get(eventId);
            if (_.isUndefined(theEvent)) {
                if (_.isUndefined(eventId) || eventId == "new") {
                    return getEventDraft();
                }
                // Create event (ready to be fetched from server)
                theEvent = new EventDetails({
                    id : eventId
                });
                getEvents().add(theEvent);
            }
            return theEvent;
        };

        var isActiveEventNotSaved = function() {
            var activeEvent = getActiveEvent();
            var activeAgenda = activeEvent ? getAgenda(activeEvent.get("id")) : undefined;
            return (activeEvent && activeEvent.hasUnsavedData()) || (activeAgenda && activeAgenda.hasUnsavedData());
        };

        var setIdForNewEvent = function(newEventId) {
            // agenda
            var newAgenda = getAgenda();
            agendas["new"] = undefined;

            newAgenda.set({
                eventId : newEventId
            });
            putAgenda(newAgenda, newEventId);

            // no persons because they cannot exist for a draft event

            // sponsors
            var newSponsors = getPartners();
            partnersPerEvent["new"] = undefined;
            newSponsors.eventId = newEventId;
            putPartners(newSponsors, newEventId);

            // servicePlan
            var newServicePlan = getServicePlan();
            servicePlanPerEvent["new"] = undefined;
            newServicePlan.eventId = newEventId;
            newServicePlan.set('event-id', newEventId);
            putServicePlan(newServicePlan, newEventId);
        };

        var getMe = function() {
            return me;
        };

        var setMe = function(person) {
            me = person;
        };

        var getAuth = function() {
            // Lazy loading because Auth init checks for a cookie
            if (_.isUndefined(auth)) {
                auth = new Auth();
            }
            return auth;
        };

        var getAgenda = function(eventId) {
            eventId = eventId || 'new';
            if (_.isUndefined(agendas[eventId])) {
                var newAgenda = new Agenda({
                    eventId : eventId
                });
                newAgenda.shouldFetch = true;
                putAgenda(newAgenda);
            }
            return agendas[eventId];
        };

        var getAgendas = function() { // used mainly for tests
            return agendas;
        };

        var putAgenda = function(agenda, eventId) {
            if (_.isUndefined(eventId)) {
                eventId = agenda.get('eventId');
            }
            agendas[eventId] = agenda;
        };

        var getPersons = function(eventId) {
            eventId = eventId || 'new';
            if (_.isUndefined(personsPerEvent[eventId])) {
                // Create persons (new persons or ready to be fetched from server)
                var newPersons = new Persons();
                newPersons.eventId = eventId;
                putPersons(newPersons);
            }
            return personsPerEvent[eventId];
        };

        var getAllPersons = function() { // used mainly for tests
            return personsPerEvent;
        };

        var putPersons = function(persons, eventId) {
            if (_.isUndefined(eventId)) {
                eventId = persons.eventId;
            }
            personsPerEvent[eventId] = persons;
        };

        var getPartners = function(eventId) {
            eventId = eventId || 'new';
            if (_.isUndefined(partnersPerEvent[eventId])) {
                // Create sponsors (new sponsors or ready to be fetched from server)
                var newPartners = new Partners();
                newPartners.eventId = eventId;
                putPartners(newPartners);
            }
            return partnersPerEvent[eventId];
        };

        var getAllPartners = function() { // used mainly for tests
            return partnersPerEvent;
        };

        var putPartners = function(partners, eventId) {
            if (_.isUndefined(eventId)) {
                eventId = partners.eventId;
            }
            partnersPerEvent[eventId] = partners;
        };

        var getServicePlan = function(eventId, packageId) {
            eventId = eventId || 'new';
            if (_.isUndefined(servicePlanPerEvent[eventId]) || packageId) {
                // Create a service plan
                var newServicePlan = new ServicePlan();
                newServicePlan.eventId = eventId;
                if (eventId == 'new') {
                    newServicePlan.set('id', packageId);
                }
                newServicePlan.forceFirstFetch = true;
                putServicePlan(newServicePlan);
            }
            return servicePlanPerEvent[eventId];
        };

        var putServicePlan = function(servicePlan, eventId) {
            if (_.isUndefined(eventId)) {
                eventId = servicePlan.eventId;
            }
            servicePlanPerEvent[eventId] = servicePlan;
        };

        var getPolls = function(eventId) {
            eventId = eventId || 'new';
            if (_.isUndefined(pollsPerEvent[eventId])) {
                var newPolls = new Polls();
                newPolls.eventId = eventId;
                newPolls.shouldFetch = true;
                putPolls(newPolls);
            }
            return pollsPerEvent[eventId];
        };

        var putPolls = function(polls, eventId) {
            if (_.isUndefined(eventId)) {
                eventId = polls.eventId
            }
            pollsPerEvent[eventId] = polls;
        };

        /***************** Init *****************/

        reset();

        /***************** Public objects *****************/

        return {
            getEvent : getEvent,
            getActiveEvent : getActiveEvent,
            setActiveEvent : setActiveEvent,
            getEventDraft : getEventDraft,
            hasEventDraft : hasEventDraft,
            getEvents : getEvents,
            setEvents : setEvents,
            reset : reset,
            setIdForNewEvent : setIdForNewEvent,

            getAgenda : getAgenda,
            getAgendas : getAgendas,
            putAgenda : putAgenda,

            getMe : getMe,
            setMe : setMe,
            getAllPersons : getAllPersons,
            getPersons : getPersons,
            putPersons : putPersons,

            getAllPartners : getAllPartners,
            getPartners : getPartners,
            putPartners : putPartners,

            getAuth : getAuth,
            isActiveEventNotSaved : isActiveEventNotSaved,

            getServicePlan : getServicePlan,
            putServicePlan : putServicePlan,

            getPolls : getPolls,
            putPolls : putPolls
        };
    };

    return ModelPool;

});
